import { useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import BulletedList from 'src/components/bulletedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Understand your blood pressure medication</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">The complete guide to heart medications</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Discover the BHF recipe collection</a></div></li>
    </ul>
  </section>
);

const personalisation_mapping = {
  k1: {
    v1: 'UNDER_30',
    v2: '30_55',
    v3: 'OVER_55',
  },
  k2: {
    v1: 'WHITE',
    v2: 'BLACK_ALL',
    v3: 'ASIAN_ALL',
    v4: 'MIXED',
    v5: 'OTHER',
  },
  k3: {
    v1: 'YES',
    v2: 'NO',
  },
  k4: {
    v1: 'YES',
    v2: 'NO',
  },
  c1: {
    v1: 'YES',
    v2: 'NO',
  },
};

const getPage = (personalisation) => {
  const { k1, k2, k4 } = personalisation;
  const age = personalisation_mapping.k1[k1];
  const ethnicity = personalisation_mapping.k2[k2];
  const multiple_conditions = personalisation_mapping.k4[k4];

  let personalisedSection = '';
  if (ethnicity === 'WHITE' || ethnicity === 'ASIAN_ALL' || ethnicity === 'MIXED' || ethnicity === 'OTHER') {
    personalisedSection = 'ACE';
  } else if (ethnicity === 'BLACK_ALL') {
    personalisedSection = 'CCB';
  } else if (age === 'UNDER_30' || age === '30_55') {
    personalisedSection = 'ACE';
  } else if (age === 'OVER_55') {
    personalisedSection = 'CCB';
  } else if (multiple_conditions === 'YES') {
    personalisedSection = 'BETA';
  } else {
    personalisedSection = 'DEFAULT';
  }

  return (
    <div className="navigator">
      <section className="navigator__content">

        <h1 className="navigator__title">
          {'Week 3: Blood pressure medication'}
        </h1>
        <p>
          {`Your doctor may have prescribed you medication to help lower your blood pressure
          and protect your heart. This week we'll help you get to grips with your medication
          and understand why it's so important.`}
        </p>
        <div className="visible-xs">{getMenu()}</div>

        {personalisedSection === 'DEFAULT' && (
          <div>
            <h2>Get to know blood pressure medication</h2>
            <section className="navigator__section-half">
              <p>
                Not everyone with high blood pressure is prescribed medication, but some people
                may be to help manage their condition. There are several different medications that
                can be used to do this. The most common ones are ACE inhibitors and calcium channel
                blockers (CCBs).
              </p>
              <p>
                Your doctor will choose the one that they believe will be most effective for managing
                your symptoms and preventing or treating a heart condition.
              </p>
            </section>
            <img alt="Tablets" className="navigator__image-half" src={`${PROGRAM_URL}image3.jpg`} />
            <p>
              It is important that you understand:
            </p>
            <NumberedList items={[
              `What your medication is, why you're taking it and what benefits it should offer`,
              'How to take your medication (including the dose, how often you should take a dose and for how long)',
              'Any side effects you should be aware of',
              'If you can take other medications at the same time if needed']
            }
            />
            <p>
              Speak to your GP, consultant or practice nurse if you feel you need more information.
            </p>
            <BulletedList items={[
              <a
                href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/ace-inhibitors"
                key="1"
                rel="noreferrer"
                target="_blank"
              >More about ACE inhibitors
              </a>,
              <a
                href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/calcium-channel-blockers"
                key="2"
                rel="noreferrer"
                target="_blank"
              >More about CCBs
              </a>,
            ]}
            />
          </div>
        )}

        {personalisedSection === 'ACE' && (
          <div>
            <h2>Understanding ACE inhibitors</h2>
            <section className="navigator__section-half">
              <p>
                Not everyone with high blood pressure is prescribed medication, but for some people it can help them manage their condition.
                There are several different medications that can be used to do this. ACE inhibitors are a common one.
              </p>
              <p>
                ACE inhibitors can help control your blood pressure by limiting an enzyme that can narrows your blood vessels.
                If your blood vessels are narrowed, this pushes your blood pressure up.
                An ACE inhibitor limits the enzyme and so the blood vessels relax and widen, lowering your blood pressure and increasing blood flow.
              </p>
              <p>
                {`Avoid eating too much salt if you're taking ACE inhibitors, because it can stop it working properly.`}
              </p>
              <p>
                Always speak to your GP,
                consultant or practice nurse if you feel you need more information or if you experience any side effects of your medication.
              </p>
            </section>
            <img className="navigator__image-half" src={`${PROGRAM_URL}image3.jpg`} />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/ace-inhibitors" rel="noreferrer" target="_blank">
              <Button
                icon="open-right"
                iconLeftAligned={false}
                text="More about ACE inhibitors"
              />
            </a>
          </div>
        )}

        {personalisedSection === 'CCB' && (
          <div>
            <h2>Understanding CCBs</h2>
            <section className="navigator__section-half">
              <p>
                Not everyone with high blood pressure is prescribed medication, but for some people it can help manage their condition.
                There are several different medications that can be used to do this. CCBs are a common one.
              </p>
              <p>
                CCB stands for calcium channel blockers.
                They are sometimes prescribed for high blood pressure because blocking calcium from getting into cells makes the blood vessels relax.
                Then the heart gets more oxygenated blood, and blood pressure can be lowered.
              </p>
              <p>
                Always speak to your GP,
                consultant or practice nurse if you feel you need more information or if you experience any side effects of your medication.
              </p>
            </section>
            <img className="navigator__image-half" src={`${PROGRAM_URL}image3.jpg`} />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/calcium-channel-blockers" rel="noreferrer" target="_blank">
              <Button
                icon="open-right"
                iconLeftAligned={false}
                text="Read more about CCBs"
              />
            </a>
          </div>
        )}

        {personalisedSection === 'BETA' && (
          <div>
            <h2>Understanding beta blockers</h2>
            <section className="navigator__section-half">
              <p>
                {`Not everyone with high blood pressure is prescribed medication, but some people are given pills called beta-blockers. They are fairly easy to take and they don't normally cause serious side effects.`}
              </p>
              <p>
                {`You're likely to be prescribed beta blockers if you have experienced angina, heart failure, a heart rhythm disorder or high blood pressure. Beta blockers lower blood pressure and heart rate and help reduce your risk of a heart attack.`}
              </p>
              <p>
                Beta blockers block the release of stress hormones called adrenaline and noradrenaline.
                This safely slows your heart rate which means less force is needed to pump blood around your body.
              </p>
              <p>
                Always speak to your GP,
                consultant or practice nurse if you feel you need more information or if you experience any side effects of your medication.
              </p>
            </section>
            <img className="navigator__image-half" src={`${PROGRAM_URL}image3.jpg`} />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/beta-blockers" rel="noreferrer" target="_blank">
              <Button
                icon="open-right"
                iconLeftAligned={false}
                text="Read more about beta blockers"
              />
            </a>
          </div>
        )}

        <h2>Heart medications and what they mean</h2>
        <section className="navigator__section-half">
          <p>
            There are lots of different medicines for different types of heart and circulatory problems.
            Each one will slightly change the way your heart or circulation works to reduce your symptoms and help your heart work better.
          </p>
          <p>{`We've put together a handy guide to possible medications you may be prescribed. It's always worth learning more about your prescription to understand its job, its side effects and how to handle any issues.`}</p>
          <p>Remember:</p>
          <NumberedList items={[
            'Always read the information that comes with your medication.',
            'Keep a list of your medicines, their dosages, and when you should take them.)',
            `Don't stop taking your medicine without talking to your doctor about it.`,
            'Check with your doctor, nurse or pharmacist before taking any other medicines, even ones you can buy without a prescription like paracetamol, ibuprofen and cough medicines.']
          }
          />
          <a href="https://www.bhf.org.uk/publications/heart-conditions/medicines-for-your-heart" rel="noreferrer" target="_blank">
            <Button
              icon="open-right"
              iconLeftAligned={false}
              text={'Read more about heart medications'}
            />
          </a>
        </section>
        <img className="navigator__image-half" src={`${PROGRAM_URL}image4.jpg`} />

        <h2>Discover the BHF recipe collection</h2>
        <section className="navigator__section-half">
          <p>{`Find hundreds of heart healthy recipes that will help the changes you make taste delicious. You can search by cuisine, preparation time and dietary requirements. We've also grouped together recipes that will help specific heart and circulatory conditions like high blood pressure so finding the right recipe couldn't be easier.`}</p>
          <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder" rel="noreferrer" target="_blank">
            <Button
              icon="open-right"
              iconLeftAligned={false}
              text="Find a recipe"
            />
          </a>
        </section>
        <img className="navigator__image-half" src={`${PROGRAM_URL}image7.jpg`} />

        <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
      </section>
    </div>
  );
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="Woman with headset" className="navigator__footer__image" src={`${PROGRAM_URL}cta-helpline.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Call our Heart Helpline
        </div>
        <div className="navigator__footer__description">
          Our Heart Helpline has Cardiac Nurses available to answer your questions. Call us on 0300 330 3311 or email <a className="navigator__footer__link" href="mailto:hearthelpline@bhf.org.uk">hearthelpline@bhf.org.uk</a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
